/**
 * The firm's reasons for cancelling, by code, for naming a passenger's reason on an order's
 * history (#464).
 *
 * A reason the firm added has a code nobody can read (`rider.firm_…`), and a starter it renamed
 * has the firm's words rather than ours, so the history needs the firm's list to say either.
 * Fetched only when an order on screen was cancelled by a passenger with a reason — most never
 * are — and then once per console session, as the line names are. A list that cannot be read
 * leaves the history on its generic words rather than on nothing.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { request, type Schema } from '@taxi/api-client';

import { cancellationReasonName } from './history';

type Reason = Schema<'CancellationReasonView'>;

let cached: Promise<Reason[] | null> | null = null;

/** Switched-off reasons included: an order may name one the firm has since retired. */
export function cancellationReasons(): Promise<Reason[] | null> {
  cached ??= request<Reason[]>('/api/orders/cancellation-reasons').catch(() => {
    // The next asker tries again rather than inheriting a failed fetch for the session.
    cached = null;
    return null;
  });

  return cached;
}

/** Forgets the list, so the next asker fetches it again. For the page that edits it. */
export function forgetCancellationReasons(): void {
  cached = null;
}

/**
 * Each reason's words by code, once the list is wanted and has arrived; undefined until then,
 * and for good when it cannot be read.
 */
export function useCancellationReasonNames(
  wanted: boolean,
): Readonly<Record<string, string>> | undefined {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState<Reason[] | null>(null);

  useEffect(() => {
    if (!wanted) return;

    let cancelled = false;

    void cancellationReasons().then((list) => {
      if (!cancelled) setReasons(list);
    });

    return () => {
      cancelled = true;
    };
  }, [wanted]);

  return useMemo(
    () =>
      reasons === null
        ? undefined
        : Object.fromEntries(
            reasons.map((reason) => [
              reason.code,
              cancellationReasonName(t, reason.code, reason.displayName, reason.namedByFirm),
            ]),
          ),
    [reasons, t],
  );
}
